import { css } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Text } from '@frontend/design-system';
import { getAchIcon, getCardBrandDisplay } from './card-brand-display';
import { formatDisplayExpiry, PaymentFieldType, toAchValue, toCardValue } from './payment-field.helpers';

const summaryStyle = css`
  display: flex;
  align-items: center;
  column-gap: var(--fabric-space-gap-sm);
  row-gap: var(--fabric-space-gap-sm);
`;

const logoStyle = css`
  width: 40px;
  height: auto;
`;

const detailsStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;

  p {
    margin-top: 0;
    margin-right: 0;
    margin-bottom: 0;
    margin-left: 0;
  }
`;

interface MaskedSummaryProps {
  type: PaymentFieldType;
  value: unknown;
}

const capitalize = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

/** Non-sensitive summary of a saved payment method (brand/bank, last four, expiry). */
export const MaskedSummary = ({ type, value }: MaskedSummaryProps) => {
  const { t } = useTranslation('digital-forms-experience');

  if (type === 'cardOnFile') {
    const card = toCardValue(value);
    const brand = getCardBrandDisplay(card.brand, t('Card'));
    const expiry = formatDisplayExpiry(card.expiryMonth, card.expiryYear);

    return (
      <div className={summaryStyle} data-testid='payment-masked-summary'>
        {brand.icon ? (
          <img src={brand.icon} alt='' className={logoStyle} />
        ) : (
          <Icon name='credit-card' size={24} color='subdued' />
        )}
        <div className={detailsStyle}>
          <Text weight='bold'>{brand.label}</Text>
          <Text size='small' color='subdued'>
            {[`*${card.lastFour}`, expiry && t('Expires {{expiry}}', { expiry })].filter(Boolean).join(' • ')}
          </Text>
        </div>
      </div>
    );
  }

  const ach = toAchValue(value);
  const achIcon = getAchIcon();
  const accountType = capitalize(ach.accountType);
  const title = [ach.bankName, accountType && t('{{accountType}} Account', { accountType })].filter(Boolean).join(' ');

  return (
    <div className={summaryStyle} data-testid='payment-masked-summary'>
      {achIcon ? <img src={achIcon} alt='' className={logoStyle} /> : <Icon name='bank' size={24} color='subdued' />}
      <div className={detailsStyle}>
        <Text weight='bold'>{title || t('Bank account')}</Text>
        <Text size='small' color='subdued'>{`*${ach.lastFour}`}</Text>
      </div>
    </div>
  );
};
